const state = {
  mode: 'masking',
  activeTool: null,
  maskCanvas: null,
  scaleX: 1,
  scaleY: 1,
  canvasWidth: 0,
  canvasHeight: 0,
  history: [],
  historyIndex: -1,
  isUndoRedoInProgress: false,
  isLoading: false,
  originalImage: null,
  resultImage: null,
  autoDetectMasks: [],
  detectedLayers: [],
  activeLayerMap: new Map()
};

const getters = {
  inpaintMode: (state) => state.mode,
  isInMaskingMode: (state) => state.mode === 'masking',
  isInResultMode: (state) => state.mode === 'result',
  inpaintActiveTool: (state) => state.activeTool,
  inpaintMaskCanvas: (state) => state.maskCanvas,
  inpaintScaleX: (state) => state.scaleX,
  inpaintScaleY: (state) => state.scaleY,
  inpaintCanvasWidth: (state) => state.canvasWidth,
  inpaintCanvasHeight: (state) => state.canvasHeight,
  inpaintHistory: (state) => state.history,
  inpaintHistoryIndex: (state) => state.historyIndex,
  canUndoInpaint: (state) => state.historyIndex > 0 && !state.isUndoRedoInProgress,
  canRedoInpaint: (state) => state.historyIndex >= 0 && state.historyIndex < state.history.length - 1 && !state.isUndoRedoInProgress,
  hasInpaintHistory: (state) => state.history.length > 1,
  isInpaintUndoRedoInProgress: (state) => state.isUndoRedoInProgress,
  isInpaintLoading: (state) => state.isLoading,
  inpaintOriginalImage: (state) => state.originalImage,
  inpaintResultImage: (state) => state.resultImage,
  inpaintAutoDetectMasks: (state) => state.autoDetectMasks,
  inpaintDetectedLayers: (state) => state.detectedLayers,
  inpaintActiveLayerMap: (state) => state.activeLayerMap,
  inpaintActiveLayers: (state) => {
    return state.detectedLayers.filter(layer => state.activeLayerMap.get(layer.id));
  },
  inpaintActiveMaskUrls: (state) => {
    const activeLayers = state.detectedLayers.filter(layer => state.activeLayerMap.get(layer.id));
    return activeLayers.map(layer => layer.maskUrl).filter(url => url);
  },
  inpaintActiveLayerIds: (state) => {
    return Array.from(state.activeLayerMap.keys()).filter(id => state.activeLayerMap.get(id));
  },
  inpaintCurrentHistorySnapshot: (state) => {
    if (state.historyIndex < 0 || state.historyIndex >= state.history.length) {
      return null;
    }
    return state.history[state.historyIndex];
  }
};

const actions = {
  setInpaintMode(context, mode) {
    context.commit('setInpaintMode', mode);
  },
  setInpaintActiveTool(context, tool) {
    context.commit('setInpaintActiveTool', tool);
  },
  setInpaintMaskCanvas(context, canvas) {
    context.commit('setInpaintMaskCanvas', canvas);
  },
  setInpaintCanvasScale(context, { scaleX, scaleY, canvasWidth, canvasHeight }) {
    context.commit('setInpaintCanvasScale', { scaleX, scaleY, canvasWidth, canvasHeight });
  },
  pushInpaintHistory(context, entry) {
    context.commit('pushInpaintHistory', entry);
  },
  clearInpaintHistory(context) {
    context.commit('clearInpaintHistory');
  },
  setInpaintLoading(context, isLoading) {
    context.commit('setInpaintLoading', isLoading);
  },
  setInpaintOriginalImage(context, imageUrl) {
    context.commit('setInpaintOriginalImage', imageUrl);
  },
  setInpaintResultImage(context, imageUrl) {
    context.commit('setInpaintResultImage', imageUrl);
  },
  setInpaintAutoDetectMasks(context, masks) {
    context.commit('setInpaintAutoDetectMasks', masks);
  },
  addDetectedLayers(context, layers) {
    context.commit('addDetectedLayers', layers);
  },
  removeDetectedLayer(context, layerId) {
    context.commit('removeDetectedLayer', layerId);
  },
  clearDetectedLayers(context) {
    context.commit('clearDetectedLayers');
  },
  resetInpaintState(context) {
    context.commit('resetInpaintState');
  },
  setUndoRedoInProgress(context, inProgress) {
    context.commit('setUndoRedoInProgress', inProgress);
  },
  setInpaintHistoryIndex(context, index) {
    context.commit('setInpaintHistoryIndex', index);
  },
  setInpaintActiveLayerMapFromIds(context, activeIds) {
    context.commit('setInpaintActiveLayerMapFromIds', activeIds);
  }
};

const mutations = {
  setInpaintMode(state, mode) {
    state.mode = mode;
  },
  setInpaintActiveTool(state, tool) {
    state.activeTool = tool;
  },
  setInpaintMaskCanvas(state, canvas) {
    state.maskCanvas = canvas;
  },

  setInpaintCanvasScale(state, { scaleX, scaleY, canvasWidth, canvasHeight }) {
    state.scaleX = scaleX;
    state.scaleY = scaleY;
    state.canvasWidth = canvasWidth;
    state.canvasHeight = canvasHeight;
  },

  pushInpaintHistory(state, entry) {
    const snapshot = entry.after != null ? entry.after : entry;
    const historyEntry = {
      id: entry.id || `entry_${Date.now()}`,
      timestamp: entry.timestamp || Date.now(),
      canvasJson: snapshot.canvasJson,
      autoDetectMasks: snapshot.autoDetectMasks,
      detectedLayers: snapshot.detectedLayers,
      activeLayerIds: snapshot.activeLayerIds,
      originalImageUrl: snapshot.originalImageUrl,
      resultImage: snapshot.resultImage ?? null,
      meta: entry.meta,
      type: entry.type
    };

    state.history = state.history.slice(0, state.historyIndex + 1);
    state.history.push(historyEntry);
    state.historyIndex = state.history.length - 1;
  },
  setInpaintHistoryIndex(state, index) {
    state.historyIndex = Math.max(-1, Math.min(index, state.history.length - 1));
  },
  setUndoRedoInProgress(state, inProgress) {
    state.isUndoRedoInProgress = inProgress;
  },
  clearInpaintHistory(state) {
    state.history = [];
    state.historyIndex = -1;
    state.isUndoRedoInProgress = false;
  },
  setInpaintLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  setInpaintOriginalImage(state, imageUrl) {
    state.originalImage = imageUrl;
  },
  setInpaintResultImage(state, imageUrl) {
    state.resultImage = imageUrl;
  },
  setInpaintAutoDetectMasks(state, masks) {
    state.autoDetectMasks = masks || [];
  },
  addDetectedLayers(state, layers) {
    state.detectedLayers.push(...layers);
    layers.forEach(layer => {
      state.activeLayerMap.set(layer.id, true);
    });
  },
  setInpaintActiveLayerMapFromIds(state, activeIds) {
    state.activeLayerMap.clear();
    (activeIds || []).forEach(id => {
      state.activeLayerMap.set(id, true);
    });
  },
  removeDetectedLayer(state, layerId) {
    state.detectedLayers = state.detectedLayers.filter(layer => layer.id !== layerId);
    state.activeLayerMap.delete(layerId);
  },
  clearDetectedLayers(state) {
    state.detectedLayers = [];
    state.activeLayerMap.clear();
  },
  resetInpaintState(state) {
    state.mode = 'masking';
    state.activeTool = null;
    state.maskCanvas = null;
    state.scaleX = 1;
    state.scaleY = 1;
    state.canvasWidth = 0;
    state.canvasHeight = 0;
    state.history = [];
    state.historyIndex = -1;
    state.isUndoRedoInProgress = false;
    state.isLoading = false;
    state.originalImage = null;
    state.resultImage = null;
    state.autoDetectMasks = [];
    state.detectedLayers = [];
    state.activeLayerMap.clear();
  }
};

export const inpaint = {
  state,
  getters,
  actions,
  mutations
};
